/**
 * Site Visit Scheduler Modal
 * Schedule field visit appointments for contacts
 */
import React, { useState } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { Calendar, Clock, User, MapPin, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

const SiteVisitScheduler = ({ contact, isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: "",
    time: "09:00",
    notes: ""
  });

  // Quick date options
  const quickDates = [
    { label: "Tomorrow", days: 1 },
    { label: "In 2 Days", days: 2 },
    { label: "In 3 Days", days: 3 },
    { label: "Next Week", days: 7 },
  ];

  const setQuickDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setFormData(prev => ({
      ...prev,
      date: date.toISOString().split('T')[0]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.date) {
      toast.error("Please select a date");
      return;
    }
    if (!formData.time) {
      toast.error("Please select a time");
      return;
    }

    setLoading(true);
    try {
      const scheduledAt = new Date(`${formData.date}T${formData.time}`).toISOString();
      
      await API.post(`/contacts/${contact.contact_id}/schedule-visit`, {
        scheduled_at: scheduledAt,
        notes: formData.notes
      });
      
      toast.success("Site visit scheduled!");
      setFormData({ date: "", time: "09:00", notes: "" });
      onSuccess();
    } catch (error) {
      toast.error("Failed to schedule visit");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose();
    toast.info("You can schedule a visit later from the contact details");
  };

  if (!contact) return null;

  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ");
  const address = [
    contact.address_street,
    contact.address_city,
    contact.address_state,
    contact.address_zip
  ].filter(Boolean).join(", ");

  // Get tomorrow's date as minimum
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <Calendar className="h-5 w-5" />
            Schedule Site Visit
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Schedule a field visit for your new contact
          </DialogDescription>
        </DialogHeader>

        {/* Contact Summary */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 space-y-2">
          <div className="flex items-center gap-2 text-white font-medium">
            <User className="h-4 w-4 text-slate-400" />
            {fullName}
          </div>
          {address && (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <MapPin className="h-4 w-4" />
              {address}
            </div>
          )}
          {contact.service_type && (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <FileText className="h-4 w-4" />
              {contact.service_type.replace(/_/g, " ")}
            </div>
          )}
        </div>

        <div className="space-y-4 py-2">
          {/* Quick Date Buttons */}
          <div>
            <Label className="text-slate-400 text-sm">Quick Select</Label>
            <div className="flex gap-2 mt-2">
              {quickDates.map(q => (
                <Button
                  key={q.days}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setQuickDate(q.days)}
                  className="border-slate-700 hover:bg-slate-800 text-slate-300"
                >
                  {q.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                min={minDate}
                className="bg-slate-800 border-slate-700 text-white [color-scheme:dark]"
              />
            </div>
            <div>
              <Label className="text-slate-400">Time *</Label>
              <Input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white [color-scheme:dark]"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-slate-400">Notes (optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Special instructions, access codes, etc."
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="ghost" onClick={handleSkip} className="text-slate-400 hover:text-slate-300">
            Skip for now
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !formData.date} 
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            <Calendar className="h-4 w-4 mr-2" />
            {loading ? "Scheduling..." : "Schedule Visit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SiteVisitScheduler;
